import React, { useState } from 'react';
import foodtruckMessages from '../utils/foodtruck.json';
import * as XLSX from 'xlsx';

const MESSAGE_STATUS = 'success'; // 알림 전송 상태

// 주문매장이름 세팅
const getShopName = (content = '') => {
  const matchedShop = content.match(/(?:^|\n)주문매장\s*:\s*([^\n\r]+)/);
  return matchedShop ? matchedShop[1].trim() : '매장 정보 없음';
};

const getFailureReason = (message) =>
  message.messageStatusName === MESSAGE_STATUS ? '' : message.messageStatusDesc || message.requestStatusDesc || '';

const getSheetName = (shopName, usedNames) => {
  const baseName = shopName.replace(/[\\/:?*[\]]/g, '_').slice(0, 31) || '매장 정보 없음';
  let sheetName = baseName;
  let suffix = 1;

  while (usedNames.has(sheetName)) {
    const suffixText = `_${suffix}`;
    sheetName = `${baseName.slice(0, 31 - suffixText.length)}${suffixText}`;
    suffix += 1;
  }

  usedNames.add(sheetName);
  return sheetName;
};

export default function Result() {
  const [openShopName, setOpenShopName] = useState(null);
  const [unitPrice, setUnitPrice] = useState('11'); // 건당 요금 상태

  // json 데이터 목록을 반복하면서 카운트 누적
  const shopCounts = foodtruckMessages.reduce((counts, message) => {
    const shopName = getShopName(message.content);

    if (!counts[shopName]) {
      counts[shopName] = { total: 0, success: 0, failure: 0 };
    }

    counts[shopName].total += 1;
    if (message.messageStatusName === MESSAGE_STATUS) {
      counts[shopName].success += 1;
    } else {
      counts[shopName].failure += 1;
    }

    return counts;
  }, {});
  const shops = Object.entries(shopCounts);

  const downloadExcel = () => {
    const workbook = XLSX.utils.book_new();
    const usedSheetNames = new Set();
    const pricePerMessage = Number(unitPrice || 0);

    shops.forEach(([shopName, counts]) => {
      const shopMessages = foodtruckMessages.filter((message) => getShopName(message.content) === shopName);
      const fee = counts.success * pricePerMessage;
      const summaryRows = [
        ['매장명', shopName],
        ['전체', counts.total],
        ['성공', counts.success],
        ['실패', counts.failure],
        ['건당 요금', pricePerMessage],
        ['비용', fee],
        [],
        ['요청일자', '상태', '실패사유', '요청 ID', '내용'],
      ];
      const detailRows = shopMessages.map((message) => [
        message.completeTime || '',
        message.messageStatusName === MESSAGE_STATUS ? '성공' : '실패',
        getFailureReason(message),
        message.requestId || '',
        message.content || '',
      ]);
      const worksheet = XLSX.utils.aoa_to_sheet([...summaryRows, ...detailRows]);

      worksheet['!cols'] = [{ wch: 20 }, { wch: 12 }, { wch: 24 }, { wch: 24 }, { wch: 80 }];
      XLSX.utils.book_append_sheet(workbook, worksheet, getSheetName(shopName, usedSheetNames));
    });

    XLSX.writeFile(workbook, '매장별_알림_전송내역.xlsx');
  };

  return (
    <main className="min-h-screen bg-cust-gray p-6">
      <section className="max-w-5xl mx-auto bg-white rounded-lg shadow p-6">
        <section>
          <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-bold text-gray-800 mb-3">매장별 알림 건수</h2>
            <div className="flex items-center">
              <label className="block mb-1 font-bold">
                건당 요금
              </label>

              <input
                type="number"
                min="0"
                value={unitPrice}
                onChange={(event) => setUnitPrice(event.target.value)}
                className="w-20 border border-gray-300 rounded px-3 py-2"
                placeholder="예: 100"
              />

              <span className="ml-2">원</span>
            </div>
            <button
              type="button"
              className="ml-3 px-3 py-2 font-bold text-white bg-primary rounded"
              onClick={downloadExcel}
            >
              엑셀 다운로드
            </button>
          </div>
          <ul className="divide-y divide-gray-200 border border-gray-200 rounded">
            {shops.map(([shopName, counts]) => {
              const shopMessages = foodtruckMessages.filter(
                (message) => getShopName(message.content) === shopName
              );

              const storeFee = counts.success * Number(unitPrice || 0); // 매장별 비용

              return (
                <li key={shopName} className="p-4 text-gray-700">
                  <div className="flex justify-between font-bold">
                    <span>{shopName}</span>
                  </div>
                  <div className="flex gap-4 mt-2 text-sm">
                    <span className="text-700">전체 {counts.total}건</span>
                    <span className="text-green-700">성공 {counts.success}건</span>
                    <span className="text-red-700">실패 {counts.failure}건</span>
                    <span className="text-blue-700">비용 {storeFee.toLocaleString()}원</span>
                  </div>
                  <button onClick={() => setOpenShopName(openShopName === shopName ? null : shopName)}>
                    {openShopName === shopName ? '닫기' : '내역 보기'}
                  </button>

                  {openShopName === shopName && (
                    <div className="mt-3 p-3 bg-gray-100 rounded">
                      <table className="w-full table-fixed border-collapse border border-gray-300">
                        <colgroup>
                          <col style={{ width: '20%' }} />
                          <col style={{ width: '50%' }} />
                          <col style={{ width: '10%' }} />
                          <col style={{ width: '20%' }} />
                        </colgroup>
                        <thead>
                          <tr>
                            <th className="border border-gray-300 px-3 py-2">요청일자</th>
                            <th className="border border-gray-300 px-3 py-2">내용</th>
                            <th className="border border-gray-300 px-3 py-2">성공 여부</th>
                            <th className="border border-gray-300 px-3 py-2">실패사유</th>
                          </tr>
                        </thead>
                        <tbody>
                          {shopMessages.map((message) => (
                            <tr key={message.requestId}>
                              <td className="border border-gray-300 px-3 py-2 text-center">{message.completeTime}</td>
                              <td className="border border-gray-300 px-3 py-2 whitespace-pre-line">{message.content}</td>
                              <td className="border border-gray-300 px-3 py-2 text-center">
                                {message.messageStatusName === 'success' ? '성공' : '실패'}
                              </td>
                              <td className="border border-gray-300 px-3 py-2">{getFailureReason(message)}</td>
                            </tr>
                          ))}

                        </tbody>
                      </table>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </section>
      </section>
    </main>
  );
}
